import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/* =========================================================
   TYPES
   ========================================================= */

export type DeliverySocketUpdateAction =
  | 'CREATED'
  | 'UPDATED'
  | 'SUBMITTED'
  | 'DELETED'
  | 'IMAGE_ADDED'
  | 'IMAGE_DELETED';

export type DeliverySocketUpdatePayload = {
  siteId: string;

  action: DeliverySocketUpdateAction;

  deliveryId: string;
};

export type DeliveryRealtimeState = {
  /*
   * Incremented every time delivery:update arrives for
   * this site.
   *
   * Components use the version change as a signal to
   * refetch authoritative Delivery data.
   */
  versionBySite: Record<string, number>;

  /*
   * Keeps information about the most recent update.
   */
  lastUpdateBySite: Record<string, DeliverySocketUpdatePayload>;
};

/* =========================================================
   INITIAL STATE
   ========================================================= */

const initialState: DeliveryRealtimeState = {
  versionBySite: {},

  lastUpdateBySite: {},
};

/* =========================================================
   SLICE
   ========================================================= */

const deliverySlice = createSlice({
  name: 'delivery',

  initialState,

  reducers: {
    deliverySocketUpdateReceived(
      state,

      action: PayloadAction<DeliverySocketUpdatePayload>
    ) {
      const payload = action.payload;

      const siteId = payload?.siteId;

      if (!siteId) {
        return;
      }

      /*
       * Increment the site's version.
       */

      state.versionBySite[siteId] = (state.versionBySite[siteId] ?? 0) + 1;

      /*
       * Remember exactly what changed.
       */

      state.lastUpdateBySite[siteId] = payload;
    },

    clearDeliveryRealtimeForSite(
      state,

      action: PayloadAction<string>
    ) {
      const siteId = action.payload;

      delete state.versionBySite[siteId];

      delete state.lastUpdateBySite[siteId];
    },
  },
});

/* =========================================================
   ACTIONS
   ========================================================= */

export const {
  deliverySocketUpdateReceived,

  clearDeliveryRealtimeForSite,
} = deliverySlice.actions;

/* =========================================================
   SELECTORS
   ========================================================= */

export const selectDeliveryRealtimeVersion = (
  state: {
    delivery?: DeliveryRealtimeState;
  },

  siteId?: string
) => {
  if (!siteId) {
    return 0;
  }

  return state.delivery?.versionBySite?.[siteId] ?? 0;
};

export const selectLastDeliveryUpdate = (
  state: {
    delivery?: DeliveryRealtimeState;
  },

  siteId?: string
) => {
  if (!siteId) {
    return undefined;
  }

  return state.delivery?.lastUpdateBySite?.[siteId];
};

/* =========================================================
   REDUCER
   ========================================================= */

export default deliverySlice.reducer;
